import React, { FC } from 'react';
import { TransactionError } from '@thirdweb-dev/react';
import { Box, Button, HStack, Text } from '@chakra-ui/react';
import { NetworkMismatchButton } from '@app/components/NetworkMismatchButton';
import { SubmitButton } from '@app/components/SubmitButton';
import { showErrorToast, showSuccessToast } from '@app/components/Toast';
import { ExternalLink } from '@app/components/ExternalLink';
import { DebouncedInput } from '@app/components/DebouncedInput';
import { LabeledValue } from '@app/components/LabeledValue';
import { formatNumber } from '@app/helpers/format';
import { useNetworkMismatch } from '@app/hooks/thirdweb/useNetworkMismatch';
import { useUnstake } from '@app/hooks/nft/useUnstake';
import { QueryKeys } from '@app/constants/queryKeys';
import { useQueryClient } from '@tanstack/react-query';

export const Unstake: FC = () => {
  const {
    state,
    handleAmountChange,
    deallocate,
    deallocating,
    resetStateValues,
    currentAllocation,
    isLoading
  } = useUnstake();

  const queryClient = useQueryClient();

  const isMismatchedNetwork = useNetworkMismatch();

  const isBalanceExceeded =
    !!currentAllocation && +currentAllocation < +state.amount;

  function getSubmitLabel() {
    if (isBalanceExceeded) {
      return 'Insufficient balance';
    }

    if (!state.amount) {
      return 'Enter xKIM amount to unstake';
    }

    return 'Unstake';
  }

  function renderSubmitAction() {
    if (isMismatchedNetwork) {
      return <NetworkMismatchButton />;
    }

    return (
      <SubmitButton
        disabled={isBalanceExceeded || !+state.amount}
        mt={4}
        onClick={async () => {
          try {
            const res = await deallocate();

            if (res['reason'] !== undefined) {
              showErrorToast(res.reason);
            } else if (res instanceof Error) {
              showErrorToast(res.message);
            } else {
              resetStateValues();

              const txHash = res?.receipt?.transactionHash;

              await queryClient.invalidateQueries({
                queryKey: [QueryKeys.GET_USER_X_KIM_ALLOCATION_TU_PLUGIN]
              });

              showSuccessToast(
                `Transaction successful`,
                <ExternalLink txHash={txHash} />
              );
            }
          } catch (e) {
            if (e instanceof TransactionError) {
              showErrorToast(e.reason);
            } else {
              showErrorToast('Transaction failed');
            }
          }
        }}
        isLoading={deallocating}
        label={getSubmitLabel()}
      />
    );
  }

  return (
    <Box mt={4}>
      <HStack justifyContent="space-between">
        <Text fontSize="16px" fontWeight={400} color="neutral.400">
          Enter xKIM amount
        </Text>
        <LabeledValue
          label="Staked xKIM:"
          isLoading={isLoading}
          value={currentAllocation ? formatNumber(+currentAllocation, 2) : '0'}
        />
      </HStack>
      <HStack
        justifyContent="space-between"
        mt={4}
        sx={{
          paddingRight: '14px',
          borderRadius: '10px',
          border: '1px solid',
          borderColor: 'gray.5',
          background: 'black'
        }}
      >
        <DebouncedInput
          amount={state.amount}
          onChange={handleAmountChange}
          textAlign="left"
        />
        <Button
          variant="tertiary"
          onClick={() => {
            if (!currentAllocation) {
              return;
            }

            handleAmountChange(currentAllocation);
          }}
          sx={{
            color: 'orange',
            bg: 'gray.2',
            width: 'fit-content',
            fontSize: '14px',
            padding: '8px 14px'
          }}
        >
          MAX
        </Button>
      </HStack>
      {renderSubmitAction()}
    </Box>
  );
};
